// Permission catalog — module-grouped metadata that powers the Roles
// tab UI. Bit values mirror PermissionFlags in
// packages/server-sdk/src/permissions.ts (1n << N). Keep both in
// sync — there's no shared source of truth yet, so the order is
// canonical here.
//
// Module groups give the Roles tab a usable layout once we ship
// 12+ permissions. Right now we have 12; this will balloon once
// each Business OS module (Mail, Calendar, Tracker, Taskboards, …)
// declares its own permission flags. The grouping is purely a UI
// convention — backend storage stays a flat bitmask.

export interface PermissionDef {
  /** Stable string key (matches PermissionFlags in @os/server-sdk). */
  key: string
  /** Bit shift offset (PermissionFlags[key] = 1n << bit). */
  bit: number
  /** Human-readable label rendered next to the checkbox. */
  label: string
  /** One-line description shown in a tooltip on hover. */
  hint?: string
}

export interface PermissionGroup {
  /** Module-style id used as React key + (later) for filter chips. */
  id: string
  /** Top-of-group section heading. */
  label: string
  /** Tailwind text colour class for the group accent. */
  accent: string
  perms: PermissionDef[]
}

/**
 * Group order = visual order in the Roles tab. Channels → Voice →
 * Moderation → Server → Admin reads top-to-bottom in increasing
 * blast radius, so a user scanning a new role can see "danger
 * grows toward the bottom".
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'channels',
    label: 'Channels',
    accent: 'text-sky-500',
    perms: [
      { key: 'VIEW_CHANNEL', bit: 0, label: 'View Channel', hint: 'See the channel in the sidebar and read its history.' },
      { key: 'SEND_MESSAGES', bit: 1, label: 'Send Messages', hint: 'Post in text channels the member can view.' },
    ],
  },
  {
    id: 'voice',
    label: 'Voice & Video',
    accent: 'text-emerald-500',
    perms: [
      { key: 'CONNECT', bit: 2, label: 'Connect (Voice)', hint: 'Join voice rooms.' },
      { key: 'SPEAK', bit: 3, label: 'Speak', hint: 'Unmute and send audio in voice rooms.' },
      { key: 'VIDEO', bit: 4, label: 'Video', hint: 'Turn camera on.' },
      { key: 'SCREEN_SHARE', bit: 5, label: 'Screen Share', hint: 'Share desktop or window.' },
    ],
  },
  {
    id: 'moderation',
    label: 'Moderation',
    accent: 'text-amber-500',
    perms: [
      { key: 'KICK_MEMBERS', bit: 9, label: 'Kick Members', hint: 'Remove members from the server (they can re-join via invite).' },
      { key: 'BAN_MEMBERS', bit: 10, label: 'Ban Members', hint: 'Permanently block by user ID or email.' },
    ],
  },
  {
    id: 'server',
    label: 'Server management',
    accent: 'text-violet-500',
    perms: [
      { key: 'MANAGE_CHANNELS', bit: 6, label: 'Manage Channels', hint: 'Create, edit, delete channels and categories.' },
      { key: 'MANAGE_ROLES', bit: 8, label: 'Manage Roles', hint: 'Create roles and assign them.' },
      { key: 'MANAGE_SERVER', bit: 7, label: 'Manage Server', hint: 'Edit server name, icon, integrations. Implies People management.' },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    accent: 'text-indigo-500',
    perms: [
      { key: 'CREATE_SHARED_BOARDS', bit: 12, label: 'Create shared boards', hint: 'Create department-wide task boards shared with the whole department. Everyone can always create their own personal boards regardless of this.' },
    ],
  },
  {
    id: 'admin',
    label: 'Administrator',
    accent: 'text-rose-500',
    perms: [
      { key: 'ADMINISTRATOR', bit: 11, label: 'Administrator', hint: 'Bypasses every check. Grant carefully.' },
    ],
  },
]

/** Flat list — needed by helpers that don't care about grouping. */
export const ALL_PERMISSIONS: PermissionDef[] = PERMISSION_GROUPS.flatMap((g) => g.perms)

/**
 * Baseline permission set for a brand-new role. Mirrors the user's
 * spec: every new role should at minimum let the member see
 * channels, post, and use voice/video/screen-share. Without this
 * a "New role" lands empty and the admin has to tick six boxes
 * manually for every common role.
 *
 * Returns the bitmask as a string (decimal) — same encoding the
 * Role.permissions column uses.
 */
export const BASELINE_PERMISSION_KEYS = [
  'VIEW_CHANNEL',
  'SEND_MESSAGES',
  'CONNECT',
  'SPEAK',
  'VIDEO',
  'SCREEN_SHARE',
] as const

export function baselinePermissionsBits(): string {
  let mask = 0n
  for (const def of ALL_PERMISSIONS) {
    if ((BASELINE_PERMISSION_KEYS as readonly string[]).includes(def.key)) {
      mask |= 1n << BigInt(def.bit)
    }
  }
  return mask.toString()
}

/* ── Bitmask helpers (string-encoded BigInt) ──────────────────── */

export function hasBit(permissions: string, bit: number): boolean {
  try {
    const v = BigInt(permissions || '0')
    return (v & (1n << BigInt(bit))) !== 0n
  } catch {
    return false
  }
}

export function setBit(permissions: string, bit: number, on: boolean): string {
  let v: bigint
  try { v = BigInt(permissions || '0') } catch { v = 0n }
  const mask = 1n << BigInt(bit)
  v = on ? (v | mask) : (v & ~mask)
  return v.toString()
}

export function toggleBit(permissions: string, bit: number): string {
  return setBit(permissions, bit, !hasBit(permissions, bit))
}
